import { kebabCase } from "@portal/app/_common/lib/changeCase";
import CN from "classnames";
import { Field, useField, useFormikContext } from "formik";

type ValidatedFormikFieldProps = React.ComponentProps<typeof Field> & {
  submitOnEnter?: boolean;
  onKeyUp?: (e: React.KeyboardEvent) => void;
};
/**
 * What's the difference between ValidatedFormikField and ValidatedFormikComponent?
 *
 * The Field version will use the Formik <Field> component to inject props onto the component
 * in `as` -- in particular, `onChange`, `onBlur`, `name`, and `value`. This is useful for
 * standard field components (like <input>).
 *
 * If you're writing your own component -- particularly one where (a) onChange behavior is already
 * handled, or where (b) onChange behavior differs from that of a standard ChangeEventHandler (i.e.
 * it does not take `event` as a parameter), Formik may crash the page. Instead, use the
 * ValidatedFormikComponent.
 *
 * Both "ValidatedField" components will inject a
 * - `hasError` boolean prop
 * - `-hasError` className
 */

export const ValidatedFormikField = ({
  name,
  className,
  "data-testid": dataTestId = kebabCase(name),
  submitOnEnter = true,
  onKeyUp,
  ...propsToField
}: ValidatedFormikFieldProps) => {
  const formik = useFormikContext();
  const [, meta] = useField(name);
  const hasError = meta.touched && !!meta.error;

  return (
    <Field
      name={name}
      className={CN(className, hasError && "-hasError")}
      data-testid={dataTestId}
      {...(propsToField.inputMode === "numeric" && {
        onKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.which < 48 || event.which > 57) {
            event.preventDefault();
          }
        },
      })}
      onKeyUp={onKeyUp}
      onKeyDown={async (e: React.KeyboardEvent) => {
        if (submitOnEnter && e.key === "Enter") {
          e.preventDefault();
          await formik.submitForm();
        }
      }}
      {...propsToField}
    />
  );
};

export const ValidatedFormikComponent = <AsComponent extends typeof Field>({
  name,
  className,
  as: Component,
  "data-testid": dataTestId = kebabCase(name),
  ...propsToComponent
}: React.ComponentProps<AsComponent> & {
  as: AsComponent;
}) => {
  const [, meta] = useField(name);
  const hasError = meta.touched && !!meta.error;

  return (
    <Component
      name={name}
      className={CN(className, hasError && "-hasError")}
      data-testid={dataTestId}
      {...propsToComponent}
    />
  );
};
